// client/src/components/Receipts/ReceiptGallery.js
// F13 – Receipt Gallery embedded in Transaction Detail / TransactionForm
// Props: transactionId (string) — required to load/upload/delete receipts.
//
// Architecture:
//  • On mount: GET /transactions/:txId/receipts → metadata list (no binary data)
//  • Thumbnails: each thumbnail lazily fetches its own base64 via GET /:receiptId
//  • Lightbox: opens the already-fetched base64; PDFs get a download link
//  • Upload: POST multipart/form-data; client validates size <5 MB before sending
//  • Delete: two-step confirm dialog → DELETE /:receiptId
//  • Max 5 receipts — upload button disabled when limit reached

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

import api from '../../services/api';

const MAX_RECEIPTS = 5;
const THUMB_SIZE = 80; // px

// ─── Thumbnail sub-component ─────────────────────────────────────────────────
// Fetches its own base64 so the parent only loads metadata on mount.
function ReceiptThumb({ transactionId, receipt, onOpen, onDelete }) {
  const [src, setSrc] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (receipt.mimeType === 'application/pdf') return; // PDF uses icon fallback

    api
      .get(`/transactions/${transactionId}/receipts/${receipt.id}`)
      .then((res) => {
        if (!cancelled) {
          const { base64, mimeType } = res.data.data;
          setSrc(`data:${mimeType};base64,${base64}`);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadErr(true);
      });

    return () => { cancelled = true; };
  }, [transactionId, receipt.id, receipt.mimeType]);

  const isPdf = receipt.mimeType === 'application/pdf';

  return (
    <Paper
      elevation={2}
      sx={{
        position: 'relative',
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: 1,
        overflow: 'hidden',
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover .receipt-overlay': { opacity: 1 },
      }}
      onClick={() => onOpen(receipt)}
    >
      {isPdf ? (
        /* PDF placeholder */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            bgcolor: 'grey.100',
            p: 0.5,
          }}
        >
          <PictureAsPdfIcon color="error" fontSize="small" />
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.55rem',
              mt: 0.5,
              textAlign: 'center',
              wordBreak: 'break-all',
              lineHeight: 1.2,
              maxHeight: 36,
              overflow: 'hidden',
            }}
          >
            {receipt.originalName}
          </Typography>
        </Box>
      ) : src ? (
        <img
          src={src}
          alt={receipt.originalName}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : loadErr ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            bgcolor: 'grey.100',
          }}
        >
          <ReceiptLongIcon color="disabled" />
        </Box>
      ) : (
        <Skeleton variant="rectangular" width={THUMB_SIZE} height={THUMB_SIZE} />
      )}

      {/* Hover overlay with Zoom + Delete icons */}
      <Box
        className="receipt-overlay"
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          opacity: 0,
          transition: 'opacity 0.18s',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          size="small"
          sx={{ color: 'white', p: 0.5 }}
          onClick={(e) => { e.stopPropagation(); onOpen(receipt, src); }}
          aria-label="View receipt"
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{ color: 'white', p: 0.5 }}
          onClick={(e) => { e.stopPropagation(); onDelete(receipt.id); }}
          aria-label="Delete receipt"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function ReceiptGallery({ transactionId }) {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Lightbox state
  const [lightbox, setLightbox] = useState(null); // { originalName, mimeType, base64 } | null
  const [lightboxLoading, setLightboxLoading] = useState(false);

  // Delete-confirm state
  const [deleteTarget, setDeleteTarget] = useState(null); // receiptId | null

  const fileInputRef = useRef(null);

  // ── Load metadata list ────────────────────────────────────────────────────
  const loadReceipts = useCallback(async () => {
    if (!transactionId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/transactions/${transactionId}/receipts`);
      setReceipts(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load receipts');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  // ── Upload ────────────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Reset input so the same file can be re-selected after a failed attempt
    e.target.value = '';
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large — maximum size is 5 MB.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const formData = new FormData();
      formData.append('receipt', file);
      const res = await api.post(
        `/transactions/${transactionId}/receipts`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setReceipts(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ── Open lightbox ─────────────────────────────────────────────────────────
  // cachedSrc is optionally passed from the thumbnail (avoids a second fetch).
  const openLightbox = async (receipt, cachedSrc) => {
    if (receipt.mimeType !== 'application/pdf' && cachedSrc) {
      // Extract base64 from existing data URL
      const base64 = cachedSrc.split(',')[1];
      setLightbox({ originalName: receipt.originalName, mimeType: receipt.mimeType, base64 });
      return;
    }

    try {
      setLightboxLoading(true);
      setLightbox({ originalName: receipt.originalName, mimeType: receipt.mimeType, base64: null });
      const res = await api.get(`/transactions/${transactionId}/receipts/${receipt.id}`);
      const { base64, mimeType, originalName } = res.data.data;
      setLightbox({ originalName, mimeType, base64 });
    } catch (err) {
      setError('Failed to load receipt');
      setLightbox(null);
    } finally {
      setLightboxLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/transactions/${transactionId}/receipts/${deleteTarget}`);
      setReceipts((prev) => prev.filter((r) => r.id !== deleteTarget));
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!transactionId) return null;

  const atLimit = receipts.length >= MAX_RECEIPTS;

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header row */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            Receipts
          </Typography>
          <Typography
            variant="caption"
            sx={{
              px: 0.75,
              py: 0.15,
              borderRadius: 10,
              bgcolor: atLimit ? 'warning.light' : 'grey.200',
              color: atLimit ? 'warning.dark' : 'text.secondary',
              fontWeight: 600,
            }}
          >
            {receipts.length}/{MAX_RECEIPTS}
          </Typography>
        </Box>

        <Tooltip title={atLimit ? 'Maximum 5 receipts per transaction' : 'Attach receipt (image or PDF)'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={uploading ? <CircularProgress size={13} color="inherit" /> : <AddIcon />}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={uploading || atLimit}
            >
              {uploading ? 'Uploading…' : 'Add'}
            </Button>
          </span>
        </Tooltip>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>

      {/* Error banner */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Gallery */}
      {loading ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width={THUMB_SIZE}
              height={THUMB_SIZE}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      ) : receipts.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', py: 0.5 }}>
          No receipts attached yet
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {receipts.map((receipt) => (
            <ReceiptThumb
              key={receipt.id}
              transactionId={transactionId}
              receipt={receipt}
              onOpen={openLightbox}
              onDelete={setDeleteTarget}
            />
          ))}
        </Box>
      )}

      {/* ── Lightbox Dialog ──────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(lightbox)}
        onClose={() => setLightbox(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 0.5,
          }}
        >
          <Typography variant="subtitle1" noWrap sx={{ maxWidth: 'calc(100% - 40px)' }}>
            {lightbox && lightbox.originalName}
          </Typography>
          <IconButton onClick={() => setLightbox(null)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            py: 2,
          }}
        >
          {lightboxLoading && <CircularProgress />}

          {!lightboxLoading && lightbox && lightbox.base64 && (
            lightbox.mimeType === 'application/pdf' ? (
              <Box sx={{ textAlign: 'center' }}>
                <PictureAsPdfIcon sx={{ fontSize: 72, color: 'error.main' }} />
                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                  PDF receipt — click below to download
                </Typography>
                <Button
                  variant="contained"
                  component="a"
                  href={`data:application/pdf;base64,${lightbox.base64}`}
                  download={lightbox.originalName}
                >
                  Download PDF
                </Button>
              </Box>
            ) : (
              <img
                src={`data:${lightbox.mimeType};base64,${lightbox.base64}`}
                alt={lightbox.originalName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 6,
                }}
              />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>Remove this receipt?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This receipt will be permanently deleted and cannot be recovered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
